import React, { useState, useEffect } from 'react';
import { orchestratorApi } from '../api/orchestrator';
import { ExclusionChip } from './ExclusionChip';
import { AddExclusionModal } from './AddExclusionModal';

interface AgentExclusionsTabProps {
  agentId: string;
  entityId?: string; // Optional - will use localStorage if not provided
  copySourceAgentId?: string;
  copySourceAgentName?: string;
}

export const AgentExclusionsTab: React.FC<AgentExclusionsTabProps> = ({
  agentId,
  entityId: propEntityId,
  copySourceAgentId,
  copySourceAgentName
}) => {
  // Get entity_id from localStorage if not provided as prop
  const [entityId, setEntityId] = useState<string>(propEntityId || '');

  useEffect(() => {
    if (!propEntityId) {
      const storedEntityId = localStorage.getItem('selectedEntityId');
      if (storedEntityId) {
        setEntityId(storedEntityId);
      }
    }
  }, [propEntityId]);

  const [groupExclusions, setGroupExclusions] = useState<Array<{
    id: number;
    participant_phone: string;
    reason: string;
    is_active: boolean;
    created_at: string;
  }>>([]);

  const [directExclusions, setDirectExclusions] = useState<Array<{
    id: number;
    direct_chat_phone: string;
    reason: string;
    is_active: boolean;
    created_at: string;
  }>>([]);

  const [groupByNameExclusions, setGroupByNameExclusions] = useState<Array<{
    id: number;
    group_chat_name: string;
    reason: string;
    is_active: boolean;
    created_at: string;
  }>>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [copying, setCopying] = useState(false);

  const fetchExclusions = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await orchestratorApi.getAgentExclusions(agentId);

      if (result.ok && result.exclusions) {
        setGroupExclusions(result.exclusions.group || []);
        setDirectExclusions(result.exclusions.direct || []);
        setGroupByNameExclusions(result.exclusions.groupByName || []);
      } else {
        setError(result.error || 'Failed to load exclusions');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load exclusions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExclusions();
  }, [agentId]);

  const handleRemoveExclusion = async (exclusionId: number) => {
    if (!confirm('Are you sure you want to remove this exclusion?')) {
      return;
    }

    try {
      const result = await orchestratorApi.deleteAgentExclusion(agentId, exclusionId);

      if (result.ok) {
        // Refresh exclusions
        fetchExclusions();
      } else {
        alert(result.error || 'Failed to remove exclusion');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to remove exclusion');
    }
  };

  const handleCopyExclusions = async () => {
    if (!copySourceAgentId || !entityId || copying) {
      return;
    }

    try {
      setCopying(true);
      const result = await orchestratorApi.copyAgentExclusions(agentId, copySourceAgentId, entityId);
      if (result.ok) {
        await fetchExclusions();
        const copied = result.copied || 0;
        const skipped = result.skipped || 0;
        alert(`Copied ${copied} exclusion${copied === 1 ? '' : 's'}${skipped > 0 ? `, skipped ${skipped} duplicate${skipped === 1 ? '' : 's'}` : ''}.`);
      } else {
        alert(result.error || 'Failed to copy exclusions');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to copy exclusions');
    } finally {
      setCopying(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '1.5rem' }}>⏳</div>
        <p style={{ color: '#6b7280', marginTop: '0.5rem' }}>Loading exclusions...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem', fontWeight: '700' }}>
          🔒 Chat Exclusions
        </h3>
        <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem' }}>
          Configure which chats this agent should ignore. Useful for excluding Operations team chats, employees, or competitors.
        </p>
        {copySourceAgentId && entityId && (
          <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button
              onClick={handleCopyExclusions}
              disabled={copying}
              style={{
                padding: '0.6rem 1rem',
                background: copying ? '#9ca3af' : '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: copying ? 'not-allowed' : 'pointer'
              }}
            >
              {copying ? 'Copying...' : `Copy Exclusions from ${copySourceAgentName || 'Source Agent'}`}
            </button>
            <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>
              Applies to the currently selected WhatsApp business session.
            </span>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          padding: '0.75rem',
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '6px',
          color: '#991b1b',
          fontSize: '0.85rem',
          marginBottom: '1.5rem'
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Group Chat Exclusions */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>
            👥 Group Chat Exclusions
          </h4>
        </div>

        <div style={{
          padding: '1.5rem',
          background: '#fef3c7',
          border: '2px dashed #f59e0b',
          borderRadius: '8px',
          marginBottom: '1rem'
        }}>
          {groupExclusions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '1rem' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>👥</div>
              <p style={{ margin: 0, color: '#92400e', fontSize: '0.9rem' }}>
                No group chat exclusions configured
              </p>
              <p style={{ margin: '0.5rem 0 0 0', color: '#b45309', fontSize: '0.8rem' }}>
                Add exclusions to prevent this agent from responding to groups containing specific phone numbers
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {groupExclusions.map((exclusion) => (
                <ExclusionChip
                  key={exclusion.id}
                  phone={exclusion.participant_phone}
                  reason={exclusion.reason}
                  type="group"
                  onRemove={() => handleRemoveExclusion(exclusion.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Direct Chat Exclusions */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>
            👤 Direct Chat Exclusions
          </h4>
        </div>

        <div style={{
          padding: '1.5rem',
          background: '#dbeafe',
          border: '2px dashed #3b82f6',
          borderRadius: '8px',
          marginBottom: '1rem'
        }}>
          {directExclusions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '1rem' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>👤</div>
              <p style={{ margin: 0, color: '#1e40af', fontSize: '0.9rem' }}>
                No direct chat exclusions configured
              </p>
              <p style={{ margin: '0.5rem 0 0 0', color: '#3b82f6', fontSize: '0.8rem' }}>
                Add exclusions to prevent this agent from responding to specific 1-to-1 chats (employees, blocked numbers, etc.)
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {directExclusions.map((exclusion) => (
                <ExclusionChip
                  key={exclusion.id}
                  phone={exclusion.direct_chat_phone}
                  reason={exclusion.reason}
                  type="direct"
                  onRemove={() => handleRemoveExclusion(exclusion.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Group by Name Exclusions */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>
            🏷️ Group by Name Exclusions
          </h4>
        </div>

        <div style={{
          padding: '1.5rem',
          background: '#f0fdf4',
          border: '2px dashed #10b981',
          borderRadius: '8px',
          marginBottom: '1rem'
        }}>
          {groupByNameExclusions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '1rem' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🏷️</div>
              <p style={{ margin: 0, color: '#065f46', fontSize: '0.9rem' }}>
                No group name exclusions configured
              </p>
              <p style={{ margin: '0.5rem 0 0 0', color: '#047857', fontSize: '0.8rem' }}>
                Add exclusions to prevent this agent from responding to specific group chats by name
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {groupByNameExclusions.map((exclusion) => (
                <ExclusionChip
                  key={exclusion.id}
                  groupName={exclusion.group_chat_name}
                  reason={exclusion.reason}
                  type="group-by-name"
                  onRemove={() => handleRemoveExclusion(exclusion.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Exclusion Button */}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <button
          onClick={() => setShowAddModal(true)}
          style={{
            padding: '0.75rem 2rem',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <span>➕</span>
          <span>Add Chat Exclusion</span>
        </button>
      </div>

      {/* Info Box */}
      <div style={{
        marginTop: '2rem',
        padding: '1rem',
        background: '#f0f9ff',
        border: '1px solid #bae6fd',
        borderRadius: '6px',
        fontSize: '0.85rem',
        color: '#0369a1'
      }}>
        <strong>ℹ️ How Exclusions Work:</strong>
        <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem' }}>
          <li><strong>Group exclusions:</strong> Agent won't respond to group chats containing the specified participant</li>
          <li><strong>Direct exclusions:</strong> Agent won't respond to 1-to-1 chats with the specified phone number</li>
          <li><strong>Group by name:</strong> Agent won't respond to specific group chats (e.g., "E&H Sales")</li>
          <li><strong>Phone format:</strong> Any format accepted (+65, spaces, dashes) - auto-normalized for you</li>
          <li><strong>Use cases:</strong> Operations team members, employees, blocked numbers, competitors, internal team chats</li>
        </ul>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <AddExclusionModal
          agentId={agentId}
          entityId={entityId}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            fetchExclusions();
            setShowAddModal(false);
          }}
        />
      )}
    </div>
  );
};
